import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import ChatSetupModal from '../../components/ChatSetup';
import {
  Container,
  Header,
  Title,
  CardsContainer,
  Card,
  CardTitle,
  Modal,
  ModalContent,
  ModalButtons,
  CancelButton,
  ConfirmButton,
  FormGroup,
  FileUploadArea,
  FormInput
} from './styles';

// Importar os ícones corretamente
import { FiCopy, FiUploadCloud, FiFileText } from 'react-icons/fi';

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.text};
`;

type ModalType = 'upload' | 'form' | null;

const Compliance: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [modalOpen, setModalOpen] = useState<ModalType>(null);
  const [file, setFile] = useState<File | null>(null);
  const [showChatSetup, setShowChatSetup] = useState(false);
  const [selectedComplianceType, setSelectedComplianceType] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    document: '',
    description: ''
  });

  // Verificar se veio da sidebar e abrir automaticamente o setup
  useEffect(() => {
    const state = location.state as any;
    if (state?.fromSidebar && state?.complianceType) {
      setSelectedComplianceType(state.complianceType);
      setShowChatSetup(true);
    }
  }, [location.state]);

  // Verificar se deve mostrar os cards (não mostrar quando vem da sidebar)
  const shouldShowCards = () => {
    const state = location.state as any;
    return !state?.fromSidebar;
  };

  const handleCardClick = (type: string) => {
    // Mostrar modal de setup antes de ir para o chat
    setSelectedComplianceType(type);
    setShowChatSetup(true);
  };

  const handleCloseModal = () => {
    setModalOpen(null);
    // Limpar dados ao fechar modal
    setFile(null);
    setFormData({
      name: '',
      document: '',
      description: ''
    });
  };

  const handleCloseChatSetup = () => {
    setShowChatSetup(false);
    setSelectedComplianceType('');
  };



  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = () => {
    // Aqui você implementaria a lógica para enviar os dados
    // Dependendo do tipo de modal aberto
    switch (modalOpen) {
      case 'upload':
        console.log('Arquivo enviado:', file);
        break;
      case 'form':
        console.log('Dados do formulário:', formData);
        break;
    }

    // Fechar o modal após o envio
    handleCloseModal();
  };

  return (
    <Container>
      {shouldShowCards() && (
        <>
          <Header>
            <Title>{t('compliance.title')}</Title>
          </Header>

          <CardsContainer>
            <Card onClick={() => handleCardClick('copy')}>
              <IconContainer>
                {FiCopy({ size: 42, className: "icon", color: theme.text })}
              </IconContainer>
              <CardTitle>{t('compliance.copyPaste')}</CardTitle>
            </Card>
            
            <Card onClick={() => handleCardClick('upload')}>
              <IconContainer>
                {FiUploadCloud({ size: 42, className: "icon", color: theme.text })}
              </IconContainer>
              <CardTitle>{t('compliance.uploadFile')}</CardTitle>
            </Card>
            
            <Card onClick={() => handleCardClick('form')}>
              <IconContainer>
                {FiFileText({ size: 42, className: "icon", color: theme.text })}
              </IconContainer>
              <CardTitle>{t('compliance.form')}</CardTitle>
            </Card>
          </CardsContainer>
        </>
      )}

      {/* Modal para "Envie um arquivo" */}
      {modalOpen === 'upload' && (
        <Modal>
          <ModalContent>
            <h3>{t('compliance.uploadFile')}</h3>
            <FormGroup>
              <label htmlFor="fileUpload">{t('compliance.selectFile')}:</label>
              <FileUploadArea onClick={() => document.getElementById('fileUpload')?.click()}>
                {FiUploadCloud({ size: 48, className: "icon", color: theme.text })}
                <p>{file ? file.name : t('compliance.dragFile')}</p>
                <p>{t('compliance.acceptedFormats')}</p>
                <input
                  type="file"
                  id="fileUpload"
                  onChange={handleFileChange}
                  accept=".pdf,.docx,.txt"
                  style={{ display: 'none' }}
                />
              </FileUploadArea>
            </FormGroup>
            <ModalButtons>
              <CancelButton onClick={handleCloseModal}>
                {t('common.cancel')}
              </CancelButton>
              <ConfirmButton onClick={handleSubmit} disabled={!file}>
                {t('compliance.send')}
              </ConfirmButton>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}

      {/* Modal para "Formulário" */}
      {modalOpen === 'form' && (
        <Modal>
          <ModalContent>
            <h3>{t('compliance.fillForm')}</h3>
            <FormGroup>
              <label htmlFor="name">{t('compliance.name')}:</label>
              <FormInput
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                placeholder={t('compliance.namePlaceholder')}
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="document">{t('compliance.document')}:</label>
              <FormInput
                type="text"
                id="document"
                name="document"
                value={formData.document}
                onChange={handleFormChange}
                placeholder={t('compliance.documentPlaceholder')}
              />
            </FormGroup>
            <FormGroup>
              <label htmlFor="description">{t('compliance.description')}:</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={t('compliance.descriptionPlaceholder')}
              />
            </FormGroup>
            <ModalButtons>
              <CancelButton onClick={handleCloseModal}>
                {t('common.cancel')}
              </CancelButton>
              <ConfirmButton onClick={handleSubmit}>
                {t('compliance.send')}
              </ConfirmButton>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}

      {/* Modal de Setup do Chat */}
      <ChatSetupModal 
        isVisible={showChatSetup}
        onClose={handleCloseChatSetup}
        complianceType={selectedComplianceType}
      />
    </Container>
  );
};

export default Compliance; 